/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import NoRecordFound from "src/components/shared/molecules/NoRecordFound";
import ModalLayout from "src/components/shared/atoms/modalLayout";
import { IPrices } from "src/components/pages/PricingPlans/dataTypes";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { useMutation } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { safeJsonParse } from "src/utils/shared/safeJson";
import { meHandler } from "src/store/reducers/meSlice";
import { useNavigate } from "react-router-dom";
import UPGRADE_SUBSCRIPTION from "src/graphql/mutation/settings/upgradeSubscription";
import DELETE_STRIPE_USER_SOFTLY from "src/graphql/mutation/settings/deleteStripeUserSoftly";
import ReducedAgents from "./reducedAgents";
import IncreasedAgents from "./increasedAgents";
import PlanSelection from "./planSelection";
import useStyle from "./Styles";

interface IPlan {
  name: string;
  productId: string;
  description: string;
  prices: IPrices[];
}

interface Props {
  upgradingPlanData?: IPlan;
  plan: string;
  handleClose: any;
  modalRef: any;
}

const UpgradePlanModal: React.FC<Props> = ({
  upgradingPlanData,
  plan,
  modalRef,
  handleClose,
}) => {
  const { classes } = useStyle();
  const { subscriptionId, currentDeletedCount } = useActiveUserDetail();
  const dispatch = useDispatch();
  const { me }: any = useSelector((state: any) => state.me);
  const navigate = useNavigate();

  const [relativeAgentsCount, setRelativeAgentsCount] = useState(0);
  const [toBeDeleteAgentsData, setToBeDeleteAgentsData] = useState<any>([]);
  const [toBeRestoredAgentsData, setToBeRestoredAgentsData] = useState<any>([]);

  const readSelectedAgentsForDeletion = (data: any) =>
    setToBeDeleteAgentsData([...data]);

  const readSelectedAgentsForRestoring = (data: any) =>
    setToBeRestoredAgentsData([...data]);

  const [selectedValues, setSelectedValues] = useState({
    interval: "month",
    intervalCount: 1,
    agentsNumber: 0,
    relativeCount: 0,
    currentUsersCount: 0,
    currentDeletedCount: 0,
  });

  const readSelectedValues = (
    _interval: string,
    _intervalCount: number,
    _agentsNumber: number,
    _relativeCount: number,
    _currentUsersCount: number,
    _currentDeletedCount: number,
  ) => {
    setSelectedValues((prev) => ({
      ...prev,
      interval: _interval,
      intervalCount: _intervalCount,
      agentsNumber: _agentsNumber,
      relativeCount: _relativeCount,
      currentUsersCount: _currentUsersCount,
      currentDeletedCount: _currentDeletedCount,
    }));
  };

  const handleCancel = () => {
    setRelativeAgentsCount(0);
    setSelectedValues((prev) => ({
      ...prev,
      ...{
        interval: "month",
        intervalCount: 1,
        agentsNumber: 0,
        relativeCount: 0,
        currentUsersCount: 0,
      },
    }));
  };

  const [
    upgradeSubscription,
    {
      loading: updateSubscriptionLoading,
      error: updateSubscriptionError,
      data: updateSubscriptionResult,
    },
  ] = useMutation(UPGRADE_SUBSCRIPTION);

  useEffect(() => {
    if (
      updateSubscriptionResult &&
      updateSubscriptionResult.updateSubscription
    ) {
      if (updateSubscriptionResult.updateSubscription) {
        if (updateSubscriptionResult.updateSubscription.success) {
          // updating ME data
          const { maxAgents, new_plan, new_plan_status, status } =
            updateSubscriptionResult.updateSubscription.result;
          dispatch(
            meHandler({
              maxCount: maxAgents,
              new_plan,
              new_plan_status,
              stripe_subscription_status: status,
            }),
          );
          //
          handleClose();
          enqueueSnackbar(
            `Plan has been successfully upgraded to ${upgradingPlanData?.name}`,
            {
              variant: "success",
            },
          );
          //
          setTimeout(() => {
            navigate("/settings/billing");
          }, 500);
        } else {
          enqueueSnackbar(updateSubscriptionResult.updateSubscription.error, {
            variant: "error",
          });
        }
      }
    }
  }, [updateSubscriptionResult]);

  useEffect(() => {
    if (updateSubscriptionError) {
      enqueueSnackbar(updateSubscriptionError.message, {
        variant: "error",
      });
    }
  }, [updateSubscriptionError]);

  const upgradePlan = () => {
    const upgradingPlanDataPricesArr = upgradingPlanData
      ? [...upgradingPlanData.prices]
      : [];
    const packagePriceBasedOnIntervalAndCount = upgradingPlanDataPricesArr.find(
      (curr) =>
        curr.interval === selectedValues.interval &&
        curr.intervalCount === selectedValues.intervalCount,
    );

    upgradeSubscription({
      variables: {
        subscriptionId,
        newPlanId: packagePriceBasedOnIntervalAndCount?.priceId,
        newQuantity: selectedValues.agentsNumber,
        billingCycleInterval: packagePriceBasedOnIntervalAndCount?.interval,
        billingCycleIntervalCount:
          packagePriceBasedOnIntervalAndCount?.intervalCount,
        newPackageName: upgradingPlanData?.name,
      },
    });
  };

  const handleUpgradePlan = () => {
    if (selectedValues.relativeCount === 0) {
      upgradePlan();
      return;
    }
    // set relative-count to show ReducedAgents screen
    if (selectedValues.relativeCount < 0)
      setRelativeAgentsCount(selectedValues.relativeCount);
    // if count is retrieved but there is no deleted users to restore, just upgrade plan
    else if (
      selectedValues.relativeCount > 0 &&
      selectedValues.currentDeletedCount === 0
    )
      upgradePlan();
    // if count is retrieved but there are deleted users to restore, set relative-count to show IncreasedAgents screen
    else setRelativeAgentsCount(selectedValues.relativeCount);
  };

  const [
    deleteUsersSoftly,
    {
      loading: deleteUsersSoftlyLoading,
      error: deleteUsersSoftlyError,
      data: deleteUsersSoftlyResult,
    },
  ] = useMutation(DELETE_STRIPE_USER_SOFTLY);

  useEffect(() => {
    if (toBeDeleteAgentsData.length > 0) {
      const agentIdArr = toBeDeleteAgentsData.map((item: { id: string }) =>
        Number(item.id),
      );
      if (
        Array.isArray(toBeDeleteAgentsData) &&
        toBeDeleteAgentsData.length > 0
      )
        deleteUsersSoftly({
          variables: {
            agentIdArr,
            toDelete: true,
            stripe_id: me?.stripe_id,
          },
        });
      // when deletion api gives positive result, then call: upgradePlan()
    }
  }, [toBeDeleteAgentsData]);

  useEffect(() => {
    if (
      deleteUsersSoftlyResult &&
      deleteUsersSoftlyResult.deleteStripeUserSoftly
    ) {
      if (deleteUsersSoftlyResult.deleteStripeUserSoftly.success) {
        const result = safeJsonParse<any>(
          deleteUsersSoftlyResult.deleteStripeUserSoftly.result,
          {},
          "Delete Users Result",
        );
        // updating ME data
        dispatch(
          meHandler({
            maxCount: result?.maxCount,
            currentCount: result?.currentCount,
            currentDeletedCount: result?.currentDeletedCount,
          }),
        );
        enqueueSnackbar(result.message, {
          variant: "success",
        });
        setRelativeAgentsCount(0);
        upgradePlan();
      } else {
        enqueueSnackbar(deleteUsersSoftlyResult.deleteStripeUserSoftly.result, {
          variant: "error",
        });
      }
    }
  }, [deleteUsersSoftlyResult]);

  useEffect(() => {
    if (deleteUsersSoftlyError) {
      enqueueSnackbar(deleteUsersSoftlyError.message, {
        variant: "error",
      });
    }
  }, [deleteUsersSoftlyError]);

  useEffect(() => {
    if (toBeRestoredAgentsData.length > 0) {
      const agentIdArr = toBeRestoredAgentsData.map((item: { id: string }) =>
        Number(item.id),
      );
      deleteUsersSoftly({
        variables: {
          agentIdArr,
          stripe_id: me?.stripe_id,
        },
      });
      // when deletion api gives positive result, then call: upgradePlan()
    } else if (
      toBeRestoredAgentsData.length === 0 &&
      selectedValues.relativeCount > 0 &&
      currentDeletedCount
    ) {
      upgradePlan();
    }
  }, [toBeRestoredAgentsData]);

  return (
    <ModalLayout
      headerText={
        <Typography className={classes.headerTextStyle}>
          Upgrade Plan
        </Typography>
      }
      ref={modalRef}
      modalCustomStyle={{
        maxWidth: "550px",
        width: "100%",
      }}
    >
      {!upgradingPlanData ? (
        <Box className={classes.notFoundBox}>
          <NoRecordFound text="no plan is selected or loaded" />
        </Box>
      ) : relativeAgentsCount === 0 ? (
        <PlanSelection
          upgradingPlanData={upgradingPlanData}
          plan={plan}
          handleClose={handleClose}
          updateSubscriptionLoading={updateSubscriptionLoading}
          handleUpgradePlan={handleUpgradePlan}
          readSelectedValues={readSelectedValues}
        />
      ) : relativeAgentsCount < 0 ? (
        <ReducedAgents
          reducedCounts={Math.abs(relativeAgentsCount)}
          currentCount={selectedValues.currentUsersCount}
          selectedCount={selectedValues.agentsNumber}
          handleDeleteAgentsData={readSelectedAgentsForDeletion}
          handleCancel={handleCancel}
          loading={deleteUsersSoftlyLoading}
        />
      ) : relativeAgentsCount > 0 && selectedValues.currentDeletedCount ? (
        <IncreasedAgents
          increasedCounts={Math.abs(relativeAgentsCount)}
          currentCount={selectedValues.currentUsersCount}
          selectedCount={selectedValues.agentsNumber}
          handleDeleteAgentsData={readSelectedAgentsForRestoring}
          handleCancel={handleCancel}
          loading={deleteUsersSoftlyLoading}
        />
      ) : null}
    </ModalLayout>
  );
};

export default UpgradePlanModal;
